import React, { memo, useMemo, useState } from "react";
import { Modal, Tabs, Avatar, Empty, Spin } from "antd";
import { useGetReader } from "../../hooks/queries/useGetReader";
import { Eye, EyeOff } from "lucide-react";
import styles from "./ReaderListModal.module.scss";

interface ReaderListModalProps {
  roomId: string;
  messageCreatedLong?: number; // Thời gian tạo tin nhắn (dạng long)
  isOpen: boolean;
  onClose: () => void;
}

export const ReaderListModal: React.FC<ReaderListModalProps> = ({ roomId, messageCreatedLong, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<string>("read");

  // Always fetch all readers (read: null), then filter on frontend
  const { data, isLoading } = useGetReader(
    {
      roomId,
      seen: messageCreatedLong,
      read: null, // Lấy tất cả
    },
    isOpen && !!roomId,
  );

  const readers = useMemo(() => data?.Data || [], [data?.Data]);

  // Tính toán số lượng đã đọc và chưa đọc
  const stats = useMemo(() => {
    const readCount = readers.filter((r) => r.Read).length;
    const unreadCount = readers.filter((r) => !r.Read).length;
    return { readCount, unreadCount };
  }, [readers]);

  // Filter readers based on active tab
  const filteredReaders = useMemo(() => {
    if (activeTab === "read") return readers.filter((r) => r.Read);
    return readers.filter((r) => !r.Read);
  }, [readers, activeTab]);

  // Build tabs - chỉ 2 tabs: Đã xem và Chưa xem
  const tabItems = useMemo(() => {
    return [
      {
        key: "read",
        label: (
          <div className={styles.tabLabel}>
            <Eye size={16} className={styles.tabIcon} />
            <span>Đã xem</span>
            <span className={styles.count}>{stats.readCount}</span>
          </div>
        ),
      },
      {
        key: "unread",
        label: (
          <div className={styles.tabLabel}>
            <EyeOff size={16} className={styles.tabIcon} />
            <span>Chưa xem</span>
            <span className={styles.count}>{stats.unreadCount}</span>
          </div>
        ),
      },
    ];
  }, [stats]);

  return (
    <Modal
      title="Danh sách người xem"
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={460}
      centered
      className={styles.readerModal}
      bodyStyle={{
        padding: 0,
        maxHeight: "500px",
      }}
    >
      <div className={styles.modalContent}>
        {/* Tabs for filtering */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          className={styles.readerTabs}
          size="small"
        />

        {/* Reader list */}
        <div className={styles.readerList}>
          {isLoading ? (
            <div className={styles.loading}>
              <Spin />
              <p>Đang tải...</p>
            </div>
          ) : filteredReaders.length === 0 ? (
            <div className={styles.empty}>
              <Empty
                description={activeTab === "unread" ? "Tất cả thành viên đã xem" : "Chưa có ai xem tin nhắn này"}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          ) : (
            filteredReaders.map((reader) => (
              <div key={reader.Id} className={styles.readerItem}>
                <Avatar src={reader.AvatarUrl} size={40} className={styles.avatar}>
                  {!reader.AvatarUrl && reader.ContactName?.charAt(0).toUpperCase()}
                </Avatar>
                <div className={styles.info}>
                  <div className={styles.name}>{reader.ContactName || reader.UserId}</div>
                  <div className={styles.description}>{reader.Description}</div>
                  {reader.Seen && (
                    <div className={styles.time}>
                      Xem lúc:{" "}
                      {new Date(reader.Seen + "Z").toLocaleString("vi-VN", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  )}
                </div>
                <div className={`${styles.status} ${reader.Read ? styles.read : styles.unread}`}>
                  {reader.Read ? <Eye size={18} /> : <EyeOff size={18} />}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
};

export default memo(ReaderListModal);
